import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams, Link, Navigate } from 'react-router-dom';
import axios from 'axios';
import SystemsHeader from '../../components/SystemsHeader';
import SystemsFooter from '../../components/SystemsFooter';
import SeoHead from '../../components/SeoHead';
import { Check, Clock, AlertCircle, ArrowRight, Mail } from 'lucide-react';

const API = `${(process.env.CRA_API_BASE_URL || process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_BACKEND_URL)}/api`;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');
  const [state, setState] = useState({ loading: true, status: null, data: null, error: null });

  const poll = useCallback(async (attempt = 0) => {
    if (attempt >= 8) {
      setState((s) => ({ ...s, loading: false, error: 'Payment status check timed out. Please check your email.' }));
      return;
    }
    try {
      const res = await axios.get(`${API}/systems/checkout/status/${sessionId}`);
      if (res.data.payment_status === 'paid') {
        setState({ loading: false, status: 'paid', data: res.data, error: null });
        return;
      }
      if (res.data.status === 'expired') {
        setState({ loading: false, status: 'expired', data: res.data, error: null });
        return;
      }
      setState({ loading: true, status: res.data.status, data: res.data, error: null });
      setTimeout(() => poll(attempt + 1), 2000);
    } catch (e) {
      setState({ loading: false, status: null, data: null, error: e.message });
    }
  }, [sessionId]);

  useEffect(() => { if (sessionId) poll(0); }, [sessionId, poll]);

  if (!sessionId) return <Navigate to="/systems/solutions" replace />;

  return (
    <div className="min-h-screen bg-white">
      <SeoHead path="/systems/payment/success" title="Payment Confirmation — ZHELTA Systems" />
      <SystemsHeader />
      <main className="py-20 md:py-32 bg-[radial-gradient(ellipse_90%_70%_at_50%_10%,#FEFBF3_0%,#ffffff_50%)]">
        <div className="zh-container max-w-2xl">
          <div className="rounded-2xl bg-white border border-gray-200 p-10 text-center shadow-[0_8px_32px_-8px_rgba(0,0,0,0.08)]">
            {state.loading && (
              <>
                <div className="w-16 h-16 mx-auto rounded-full bg-amber-100 border-4 border-[#f5a623] flex items-center justify-center animate-pulse">
                  <Clock className="w-7 h-7 text-[#c9a227]" strokeWidth={2} />
                </div>
                <h1 className="mt-5 text-2xl font-bold text-gray-900">Confirming your payment…</h1>
                <p className="mt-2 text-gray-600">This takes a few seconds. Do not close this tab.</p>
              </>
            )}

            {state.status === 'paid' && (
              <div data-testid="payment-success">
                <div className="w-16 h-16 mx-auto rounded-full bg-emerald-100 border-4 border-emerald-500 flex items-center justify-center">
                  <Check className="w-7 h-7 text-emerald-600" strokeWidth={3} />
                </div>
                <h1 className="mt-5 text-3xl font-extrabold text-gray-900">Payment confirmed</h1>
                <p className="mt-2 text-gray-600">Thank you for subscribing to <strong>{state.data?.service_name}</strong> — <strong>{state.data?.tier_name}</strong>.</p>
                <div className="mt-6 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gray-50 border border-gray-200 text-[13px] text-gray-700">
                  <Mail className="w-3.5 h-3.5" /> A receipt has been emailed to you.
                </div>
                <div className="mt-6 text-[13px] text-gray-500 tracking-wider">Reference: {sessionId.slice(-10)}</div>
                <div className="mt-8 flex flex-wrap gap-3 justify-center">
                  <Link to="/systems" className="px-6 py-3 rounded-full bg-[#f5a623] text-[#0e0e10] text-[13px] font-semibold uppercase tracking-wider hover:brightness-105 transition inline-flex items-center gap-2">Back to Systems <ArrowRight className="w-4 h-4" /></Link>
                  <Link to="/systems/book-demo" className="px-6 py-3 rounded-full bg-[#f5a623] text-black text-[13px] font-semibold uppercase tracking-wider hover:brightness-110 transition">Book onboarding call</Link>
                </div>
              </div>
            )}

            {state.status === 'expired' && (
              <>
                <div className="w-16 h-16 mx-auto rounded-full bg-gray-100 border-4 border-gray-300 flex items-center justify-center">
                  <AlertCircle className="w-7 h-7 text-gray-500" strokeWidth={2} />
                </div>
                <h1 className="mt-5 text-2xl font-bold text-gray-900">Checkout session expired</h1>
                <p className="mt-2 text-gray-600">Your payment session timed out. Please try again.</p>
                <Link to="/systems/solutions" className="mt-6 inline-block px-6 py-3 rounded-full bg-[#f5a623] text-black text-[13px] font-semibold uppercase tracking-wider hover:brightness-110 transition">Choose a plan</Link>
              </>
            )}

            {state.error && (
              <>
                <div className="w-16 h-16 mx-auto rounded-full bg-red-100 border-4 border-red-300 flex items-center justify-center">
                  <AlertCircle className="w-7 h-7 text-red-500" strokeWidth={2} />
                </div>
                <h1 className="mt-5 text-2xl font-bold text-gray-900">Something went wrong</h1>
                <p className="mt-2 text-gray-600">{state.error}</p>
                <Link to="/systems/contact" className="mt-6 inline-block px-6 py-3 rounded-full bg-[#f5a623] text-[#0e0e10] text-[13px] font-semibold uppercase tracking-wider hover:brightness-105">Contact support</Link>
              </>
            )}
          </div>
        </div>
      </main>
      <SystemsFooter />
    </div>
  );
}
